import { Grid, TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { formatDate } from 'helpers/StringNumberFunction/FormatDate'
import { amtStr } from 'helpers/StringNumberFunction/NumFormatters'
import React, { useMemo, useEffect } from 'react'
import { Controller } from 'react-hook-form'
import NumberFormat from 'react-number-format'

export const AssetsRegisterDepreciationComponent = (props: any) => {
  const {
    editData,
    mode,
    watch,
    setValue,
    trigger,
    errors,
    control,
    register,
    accPeriodYears,
    getAccountPeriod,
    AssetsRegistrationID,
    depreciationAmt,
    usefulLife,
    utilizedUsefulLife,
    getFADepreciation,
    getFADepreciationLoading,
    accountPeriodLoading,
    selectedMethod,
    setSelectedMethod,
    MethodID,
    setMethodID,
    setStartYear,
    setStartPeriod,
    setDepreciationAmt,
    setUsefulLife,
    setUtilizedUsefulLife,
    baseOriginalAmount = 0,
    residualValue = 0,
  } = props

  const maxDepreciableAmount = useMemo(() => {
    const boa = parseFloat(String(baseOriginalAmount).replace(/,/g, '')) || 0
    const rv = parseFloat(String(residualValue).replace(/,/g, '')) || 0
    return Math.max(0, boa - rv)
  }, [baseOriginalAmount, residualValue])

  const openingNetBookValue = useMemo(() => {
    const boa = parseFloat(String(baseOriginalAmount).replace(/,/g, '')) || 0
    const accDep = parseFloat(String(depreciationAmt).replace(/,/g, '')) || 0
    return Math.max(0, boa - accDep)
  }, [baseOriginalAmount, depreciationAmt])

  const accDepreciationError = useMemo(() => {
    const accDep = parseFloat(String(depreciationAmt).replace(/,/g, '')) || 0
    if (accDep > maxDepreciableAmount && maxDepreciableAmount > 0) {
      return `Opening Acc. Depreciation cannot exceed depreciable amount (${amtStr(
        maxDepreciableAmount
      )}). Please adjust the amount before submitting.`
    }
    return ''
  }, [depreciationAmt, maxDepreciableAmount])

  const getStartYearOptions = () => {
    const startYears = Array.from(accPeriodYears)
    const stringStartYears = startYears.map(year => String(year))
    stringStartYears.sort((a: any, b: any) => b - a)
    return stringStartYears
  }

  const getFinancialAccountPeriod = getAccountPeriod?.filter(
    x => String(x?.FYear) === String(watch('StartYear'))
  )

  const handleStartYearChange = (value, newValue: any) => {
    setValue('StartYear', newValue)
  }

  const handleDepreciationAmtChange = event => {
    setDepreciationAmt(event.value)
  }

  const handleUsefulLifeChange = event => {
    const newValue = event.value || 0
    setUsefulLife(newValue)
    setValue('UsefulLife', newValue)
  }

  const handleUtilizedUsefulLifeChange = event => {
    const newValue = event.value || 0
    setUtilizedUsefulLife(newValue)
    setValue('UtilizedUsefulLife', newValue)
  }

  useEffect(() => {
    if (usefulLife !== undefined && usefulLife !== null) {
      setValue('UsefulLife', usefulLife)
    }
  }, [usefulLife, setValue])

  useEffect(() => {
    if (utilizedUsefulLife !== undefined && utilizedUsefulLife !== null) {
      setValue('UtilizedUsefulLife', utilizedUsefulLife)
    }
  }, [utilizedUsefulLife, setValue])

  useEffect(() => {
    if (MethodID) {
      setValue('MethodID', MethodID)
      const matchedMethod = getFADepreciation?.find(
        method => method.fa_depreciation_method_id === MethodID
      )
      if (matchedMethod && !selectedMethod) {
        setSelectedMethod(matchedMethod)
      }
      if (trigger) {
        setTimeout(() => trigger('MethodID'), 100)
      }
    }
  }, [MethodID, setValue, trigger, getFADepreciation])

  return (
    <>
      {!getFADepreciationLoading && (
        <Controller
          render={({ value, onChange }) => {
            const defVal = getFADepreciation?.filter(
              coa =>
                coa?.fa_depreciation_method_id ===
                editData?.fa_depreciation_method_id
            )[0]

            const currentValue = value
              ? getFADepreciation?.find(
                  method => method?.fa_depreciation_method_id === value
                )
              : MethodID
              ? getFADepreciation?.find(
                  method => method?.fa_depreciation_method_id === MethodID
                )
              : selectedMethod || defVal || null

            return (
              <Autocomplete
                options={
                  getFADepreciation?.sort((a, b) => {
                    return a?.name?.localeCompare(b?.name)
                  }) || []
                }
                getOptionLabel={option => {
                  return `${option?.name}`
                }}
                fullWidth
                value={currentValue || null}
                onChange={(event, newValue: any) => {
                  if (!newValue) {
                    onChange('')
                    setSelectedMethod(null)
                    setMethodID('')
                    setValue('MethodID', '')
                  } else {
                    onChange(newValue?.fa_depreciation_method_id)
                    setSelectedMethod(newValue)
                    setMethodID(newValue?.fa_depreciation_method_id)
                    setValue('MethodID', newValue?.fa_depreciation_method_id)
                    if (trigger) {
                      setTimeout(() => trigger('MethodID'), 0)
                    }
                  }
                }}
                disabled={mode === 'approve-reject'}
                renderOption={(props, option) => {
                  return (
                    <div>
                      <div>
                        <span className="xsTitle">{props?.name}</span>
                      </div>
                    </div>
                  )
                }}
                renderInput={(params: any) => {
                  return (
                    <div>
                      <TextField
                        {...params}
                        label="Method"
                        style={{ width: '100%' }}
                        margin="normal"
                        helperText={errors?.MethodID?.message}
                        error={errors?.MethodID ? true : false}
                        required
                      />
                    </div>
                  )
                }}
              />
            )
          }}
          name="MethodID"
          label="Method"
          autoComplete="off"
          control={control}
          ref={register}
          defaultValue={editData?.fa_depreciation_method_id}
          multiline={true}
          fullWidth
          margin="normal"
          helperText={errors?.MethodID?.message}
          error={errors?.MethodID ? true : false}
          required
        />
      )}

      <Grid
        item
        xs={6}
        style={{
          paddingRight: '10px',
        }}
      >
        {!accountPeriodLoading && (
          <Controller
            render={({ value, onChange }) => {
              // const defVal = getRecurringJournal?.filter(
              //   x =>
              //     x?.RecurringJournalID === editData?.RecurringJournalID
              // )[0]
              return (
                <Autocomplete
                  options={getStartYearOptions()}
                  getOptionLabel={option => option}
                  fullWidth
                  onChange={(value, newValue: any) => {
                    if (!newValue) {
                      onChange('')
                      handleStartYearChange(value, '')
                      setValue('StartPeriod', '')
                      setStartYear('')
                    } else {
                      onChange(newValue)
                      handleStartYearChange(value, newValue)
                      setValue('StartYear', Number(newValue))
                      setStartYear(Number(newValue))
                    }
                  }}
                  renderOption={(props, option) => {
                    return <span>{props}</span>
                  }}
                  defaultValue={
                    editData?.depreciation_start_year
                      ? String(editData?.depreciation_start_year)
                      : ''
                  }
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          type="number"
                          label="Start Year"
                          style={{ width: '100%' }}
                          margin="dense"
                          helperText={errors?.StartYear?.message}
                          error={errors?.StartYear ? true : false}
                          required
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            name="StartYear"
            label="Start Year"
            autoComplete="off"
            control={control}
            ref={register}
            multiline={true}
            fullWidth
            margin="dense"
            helperText={errors?.StartYear?.message}
            error={errors?.StartYear ? true : false}
            required
          />
        )}
      </Grid>

      <Grid item xs={6}>
        <Controller
          render={({ value, onChange }) => {
            return (
              <Autocomplete
                options={getFinancialAccountPeriod || []}
                getOptionLabel={option => String(option?.FPeriod)}
                fullWidth
                value={
                  getFinancialAccountPeriod.find(
                    option => option?.FPeriod === value
                  ) || null
                }
                onChange={(value, newValue: any) => {
                  if (!newValue) {
                    onChange('')
                    setStartPeriod('')
                  } else {
                    onChange(newValue?.FPeriod)
                    setStartPeriod(newValue?.FPeriod)
                  }
                }}
                renderOption={(props, option) => {
                  return (
                    <div>
                      <div>
                        <span className="xsTitle">{props?.FPeriod}</span>
                      </div>
                      <div className="desc">
                        {`${formatDate(props?.StartDate)} - ${formatDate(
                          props?.EndDate
                        )}`}
                      </div>
                    </div>
                  )
                }}
                renderInput={(params: any) => {
                  return (
                    <div>
                      <TextField
                        {...params}
                        type="number"
                        label="Start Period"
                        style={{ width: '100%' }}
                        margin="dense"
                        helperText={errors?.StartPeriod?.message}
                        error={errors?.StartPeriod ? true : false}
                        required
                      />
                    </div>
                  )
                }}
              />
            )
          }}
          name="StartPeriod"
          label="Start Period"
          autoComplete="off"
          control={control}
          ref={register}
          defaultValue={editData?.depreciation_start_period ?? ''}
          multiline={true}
          fullWidth
          margin="dense"
          helperText={errors?.StartPeriod?.message}
          error={errors?.StartPeriod ? true : false}
          required
          disabled={mode === 'approve-reject' || !watch('StartYear')}
        />
      </Grid>

      <Controller
        as={<NumberFormat allowNegative={false} />}
        customInput={TextField}
        id="standard-basic"
        name="DepreciationAmt"
        label="Opening Acc. Depreciation"
        autoComplete="off"
        control={control}
        ref={register}
        value={depreciationAmt}
        onValueChange={e => {
          handleDepreciationAmtChange(e)
        }}
        thousandSeparator
        decimalScale={2}
        fixedDecimalScale
        margin="normal"
        helperText={
          accDepreciationError || `Max: ${amtStr(maxDepreciableAmount)}`
        }
        error={!!accDepreciationError}
      />

      <Controller
        as={<NumberFormat allowNegative={false} />}
        customInput={TextField}
        id="standard-basic"
        name="UsefulLife"
        label="Useful Life (Period)"
        autoComplete="off"
        control={control}
        ref={register}
        value={usefulLife}
        onValueChange={e => {
          handleUsefulLifeChange(e)
        }}
        thousandSeparator
        // decimalScale={2}
        // fixedDecimalScale
        margin="normal"
        className="left"
        helperText={errors?.UsefulLife?.message}
        error={errors?.UsefulLife ? true : false}
        required
      />

      <Controller
        as={<NumberFormat allowNegative={false} />}
        customInput={TextField}
        id="utilized-useful-life"
        name="UtilizedUsefulLife"
        label="Utilized Useful Life (Period)"
        autoComplete="off"
        control={control}
        ref={register}
        value={utilizedUsefulLife}
        onValueChange={e => {
          handleUtilizedUsefulLifeChange(e)
        }}
        thousandSeparator
        margin="normal"
        className="right"
        helperText={errors?.UtilizedUsefulLife?.message}
        error={errors?.UtilizedUsefulLife ? true : false}
      />
    </>
  )
}
